import React, { Fragment } from 'react'

const statusStyles = {
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  suspended: 'bg-amber-500/10 text-amber-600 dark:text-amber-300',
  locked: 'bg-rose-500/10 text-rose-600 dark:text-rose-300',
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const UserListTable = ({
  users = [],
  selectedUserId,
  onSelectUser,
  onAction,
  actionDescriptors = [],
  isLoading = false,
  totalCount = users.length,
  startIndex = users.length ? 1 : 0,
  isMobileView = false,
}) => {
  const renderStatus = status => {
    const key = status?.toLowerCase()
    const styles =
      statusStyles[key] ?? 'bg-slate-500/10 text-slate-600 dark:text-slate-200'
    return (
      <span
        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${styles}`}
      >
        {status}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-xl backdrop-blur dark:border-white/5 dark:bg-customSurface">
        <div className="flex items-center justify-between border-b border-customMuted/30 px-4 py-3 dark:border-white/10">
          <h2 className="text-lg font-semibold text-customInk dark:text-customHeading">
            User Directory
          </h2>
          <span className="text-sm text-customSecondaryText dark:text-customMuted">
            Loading...
          </span>
        </div>
        <div className="space-y-4 p-6">
          {[...Array(4)].map((_, idx) => (
            <div
              key={`skeleton-${idx}`}
              className="flex animate-pulse items-center justify-between rounded-2xl bg-customSlate/60 p-4 dark:bg-white/5"
            >
              <div className="h-4 w-24 rounded-full bg-white/50 dark:bg-white/10" />
              <div className="h-4 w-20 rounded-full bg-white/50 dark:bg-white/10" />
              <div className="h-4 w-16 rounded-full bg-white/50 dark:bg-white/10" />
              <div className="h-4 w-28 rounded-full bg-white/50 dark:bg-white/10" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!users.length) {
    return (
      <div className="flex h-full min-h-[250px] flex-col items-center justify-center rounded-2xl border border-dashed border-customMuted/40 bg-white/70 p-6 text-center shadow-sm dark:border-white/10 dark:bg-customSurface">
        <p className="text-base font-semibold text-customPrimaryText dark:text-customWhite">
          No users match your filters
        </p>
        <p className="text-sm text-customSecondaryText dark:text-customMuted">
          Update the search filters to see user profiles.
        </p>
      </div>
    )
  }

  const renderActionButtons = (user, extraClasses = '') => (
    <div className={`flex items-center gap-1 text-customMuted ${extraClasses}`}>
      {actionDescriptors.map(action => {
        const Icon = action.icon
        return (
          <button
            key={action.id}
            type="button"
            onClick={event => {
              event.stopPropagation()
              onAction(action.id, user)
            }}
            className={`rounded-full border bg-background/5 border-transparent p-[6px] transition hover:scale-105 hover:bg-customSlate/80 dark:hover:bg-white/10 ${action.accent}`}
            title={action.label}
            aria-label={action.label}
          >
            <Icon className="h-5 w-5" />
          </button>
        )
      })}
    </div>
  )

  return (
    <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-xl backdrop-blur dark:border-white/5 dark:bg-customSurface">
      <div className="flex items-center justify-between border-b border-customMuted/30 px-4 py-3 dark:border-white/10">
        <h2 className="text-lg font-semibold text-customInk dark:text-customHeading">
          User Table
        </h2>
        <span className="text-sm text-customAccentBlue dark:text-customAccent">
          {totalCount} {totalCount === 1 ? 'profile' : 'profiles'}
        </span>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-white/5 ">
          <thead className="bg-background text-left text-xs font-semibold uppercase tracking-wide text-customLightMilkWhite dark:bg-white/5">
            <tr>
              <th className="px-4 py-3">No</th>
              <th className="px-4 py-3">User ID</th>
              <th className="px-4 py-3">Username</th>
              {/* <th className="px-4 py-3">Role</th> */}
              {isMobileView ? null : <th className="px-4 py-3 text-right">Balance</th>}
              <th className="px-4 py-3">Status</th>
              <th className="hidden px-4 py-3 text-center md:table-cell">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white/80 dark:divide-white/5 dark:bg-transparent">
            {users.map((user, index) => {
              const isSelected = selectedUserId === user.id
              return (
                <Fragment key={user.id}>
                <tr
                  key={user.id}
                  className={`cursor-pointer transition hover:bg-customSlate/60 dark:hover:bg-white/5 ${
                    isSelected
                      ? 'bg-customSlate/70 dark:bg-white/10'
                      : 'bg-transparent'
                  }`}
                  onClick={() => onSelectUser(user.id)}
                >
                  <td className="px-4 py-4 text-xs font-semibold text-customMuted">
                    {startIndex + index}
                  </td>
                  <td className="px-4 py-4 font-semibold text-customInk dark:text-customHeading">
                    {user.id}
                  </td>
                  <td className="px-4 py-4 text-customPrimaryText dark:text-customWhite">
                    <p className='font-semibold'>{user.username}</p>
                    <p className='text-customSecondaryText text-xs'>{user.role}</p>
                  </td>
                  {/* <td className="px-4 py-4 text-customSecondaryText dark:text-customMuted">
                    {user.role}
                  </td> */}
                  {isMobileView ? null : <td className="px-4 py-4 text-right font-semibold text-customFontGreen">
                    {currencyFormatter.format(user.balance ?? 0)}
                  </td>}
                  <td className="px-4 py-4">{renderStatus(user.status)}</td>
                  <td className="hidden px-4 py-4 md:table-cell">
                    {renderActionButtons(user, 'justify-center')}
                  </td>
                </tr>
                { isMobileView ? (
                  <tr
                    key={`${user.id}-actions`}
                    className={`md:hidden ${
                      isSelected
                        ? 'bg-customSlate/70 dark:bg-white/10'
                        : 'bg-transparent'
                    }`}
                  >
                    <td colSpan={3} className="px-4 pb-4">
                      {renderActionButtons(user, 'justify-start flex-wrap')}
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-customFontGreen">
                    {currencyFormatter.format(user.balance ?? 0)}
                  </td>
                  </tr>
                ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default UserListTable
